import { getCurrentUser, User } from "./pricingUtils";

export type ProductCategoryKey = "spare_part" | "tire" | "battery" | "oil";

export const PRODUCT_CATEGORY_OPTIONS: {
  key: ProductCategoryKey;
  label: string;
}[] = [
  { key: "spare_part", label: "Spare Part" },
  { key: "tire", label: "Tire" },
  { key: "battery", label: "Battery" },
  { key: "oil", label: "Oil" },
];

const PRODUCT_CATEGORY_MAP: Record<string, ProductCategoryKey> = {
  "spare part": "spare_part",
  spare_part: "spare_part",
  "spare parts": "spare_part",
  goma: "tire",
  tire: "tire",
  tyre: "tire",
  bateri: "battery",
  battery: "battery",
  oil: "oil",
};

const TECDOC_CATEGORY_MAP: Record<string, ProductCategoryKey> = {
  "3465": "battery",
  "1862": "oil",
  "3224": "oil",
};

export function normalizeCategoryKey(
  value?: string | null
): ProductCategoryKey | null {
  if (!value) return null;
  const raw = String(value).trim().toLowerCase();
  const underscored = raw.replace(/-/g, "_").replace(/\s+/g, "_");
  if (
    underscored === "spare_part" ||
    underscored === "tire" ||
    underscored === "battery" ||
    underscored === "oil"
  ) {
    return underscored;
  }
  return PRODUCT_CATEGORY_MAP[raw] || null;
}

export function resolveProductCategoryKey(ctx?: {
  pathname?: string;
  urlCategory?: string | number | null;
  productCategory?: string | null;
  hint?: string | null;
}): ProductCategoryKey {
  const path = (ctx?.pathname || window.location.pathname || "").toLowerCase();
  if (path.includes("search-tyre") || path.includes("search-tire")) {
    return "tire";
  }
  if (path.includes("search-oil")) {
    return "oil";
  }
  if (path.includes("search-battery")) {
    return "battery";
  }

  if (ctx?.urlCategory != null && ctx.urlCategory !== "") {
    const fromTecdoc = TECDOC_CATEGORY_MAP[String(ctx.urlCategory)];
    if (fromTecdoc) return fromTecdoc;
  }

  // Also check current URL query for category=
  try {
    const params = new URLSearchParams(window.location.search);
    const qCat = params.get("category");
    if (qCat && TECDOC_CATEGORY_MAP[qCat]) {
      return TECDOC_CATEGORY_MAP[qCat];
    }
  } catch {
    // ignore
  }

  const fromProduct = normalizeCategoryKey(ctx?.productCategory);
  if (fromProduct) return fromProduct;

  const fromHint = normalizeCategoryKey(ctx?.hint);
  if (fromHint) return fromHint;

  // Vehicle / brand / keyword TecDoc search defaults to spare parts
  return "spare_part";
}

export function getUserAllowedCategories(
  user: User | null = getCurrentUser()
): ProductCategoryKey[] {
  const raw = (user as any)?.allowed_categories;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => normalizeCategoryKey(c))
    .filter((c): c is ProductCategoryKey => Boolean(c));
}

/** Price + basket allowed for this category */
export function canAccessCategoryPricing(
  categoryKey: ProductCategoryKey,
  user: User | null = getCurrentUser()
): boolean {
  if (!user) return false;
  if ((user as any).is_admin || (user as any).is_super_admin) return true;
  return getUserAllowedCategories(user).includes(categoryKey);
}

export function canSeeProductPrice(
  article?: { category?: string | null },
  user: User | null = getCurrentUser()
): boolean {
  const key = resolveProductCategoryKey({
    productCategory: article?.category,
  });
  return canAccessCategoryPricing(key, user);
}

export function canAddProductToBasket(
  article?: { category?: string | null },
  user: User | null = getCurrentUser()
): boolean {
  return canSeeProductPrice(article, user);
}
